import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PrinterIcon, XIcon } from "./Icons";
import { tick } from "../fx";

const PRINT_MS = 2200;
const START_DELAY = 420; // let the machine finish popping in first
// tick offsets across one print run — they bunch up as the feed speeds up
const TICKS = [0, 24, 49, 75, 103, 133, 166, 202, 242, 286, 336, 392, 456, 530, 615, 712, 822, 948, 1092, 1256, 1442, 1652, 1888, 2070, 2160, 2195];
const TICK_EVERY_PX = 48;

const reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Torn-off bottom edge: a zig-zag clip path `depth` px deep. */
const teeth = (depth: number) => {
  const pts: string[] = [];
  for (let r = 100; r >= 0; r -= 1.25) {
    const up = Math.round(r / 1.25) % 2 === 1;
    pts.push(up ? `${r.toFixed(3)}% calc(100% - ${depth}px)` : `${r.toFixed(3)}% 100%`);
  }
  return `polygon(0% 0%, 100% 0%, ${pts.join(", ")})`;
};

/* ---------------- hanging tag ---------------- */

export const PrinterSign: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={onClick}
    aria-label="Print this portfolio"
    title="Print this portfolio"
    className="printer-sign group fixed right-8 top-0 z-[100] hidden sm:flex cursor-pointer flex-col items-center"
  >
    <svg width="16" height="38" viewBox="0 0 16 38" fill="none" aria-hidden="true" className="text-(--border)">
      <line x1="8" y1="0" x2="8" y2="36" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
    <span className="relative -mt-[3px] flex size-[46px] items-center justify-center rounded-[8px] border border-(--border) bg-(--bg) pt-1.5 text-(--dim) shadow-sm transition-colors duration-300 group-hover:border-(--muted) group-hover:text-(--muted)">
      <span className="absolute left-1/2 top-[3px] size-[3px] -translate-x-1/2 rounded-full bg-(--border)" />
      <PrinterIcon />
    </span>
  </button>
);

/* ---------------- paper ---------------- */

const Paper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [phase, setPhase] = useState<"idle" | "grow" | "settle">("idle");
  const [feed, setFeed] = useState(0); // 0..1 — how far the reader has pulled the sheet back through the slot
  const [scrubbing, setScrubbing] = useState(false);
  const feedRef = useRef(0);
  const travelled = useRef(0);
  const visible = 0.95 * height;

  // keep the sheet length current as images and the heatmap load in
  useEffect(() => {
    const el = ref.current!;
    const measure = () => setHeight(el.scrollHeight);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  // the print run: feed out with accelerating ticks, then settle with a small overshoot
  useEffect(() => {
    if (reducedMotion()) {
      setPhase("settle");
      return;
    }
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        setPhase("grow");
        TICKS.forEach((ms) => timers.push(window.setTimeout(tick, ms)));
        timers.push(window.setTimeout(() => setPhase("settle"), PRINT_MS));
      }, START_DELAY),
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // wheel / touch pulls the sheet back up through the slot, ticking as it goes
  useEffect(() => {
    if (!visible) return;
    let idle = 0;
    const move = (px: number) => {
      const next = Math.min(1, Math.max(0, feedRef.current + px / visible));
      if (next === feedRef.current) return;
      travelled.current += Math.abs(next - feedRef.current) * visible;
      if (travelled.current >= TICK_EVERY_PX) {
        travelled.current %= TICK_EVERY_PX;
        tick();
      }
      feedRef.current = next;
      setFeed(next);
      setScrubbing(true);
      clearTimeout(idle);
      idle = window.setTimeout(() => setScrubbing(false), 120);
    };
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || !e.deltaY) return;
      e.preventDefault();
      move(e.deltaMode === 1 ? e.deltaY * 16 : e.deltaMode === 2 ? e.deltaY * innerHeight : e.deltaY);
    };
    let lastY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (lastY === null || y === undefined) return;
      e.preventDefault();
      move(lastY - y);
      lastY = y;
    };
    addEventListener("wheel", onWheel, { passive: false });
    addEventListener("touchstart", onTouchStart, { passive: true });
    addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      clearTimeout(idle);
      removeEventListener("wheel", onWheel);
      removeEventListener("touchstart", onTouchStart);
      removeEventListener("touchmove", onTouchMove);
    };
  }, [visible]);

  const max = phase === "idle" ? 0 : phase === "grow" ? 1.02 * visible : visible;
  const shown = max * (1 - feed);
  const timing = scrubbing
    ? "0s"
    : phase === "grow"
      ? `${PRINT_MS}ms cubic-bezier(0.3, 0, 0.2, 1)`
      : phase === "settle"
        ? "0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
        : "0s";

  return (
    <div
      className="absolute left-1/2 top-1/2 z-10 max-w-[95%] -translate-x-1/2 overflow-hidden"
      style={{ width: 798, maxHeight: shown, transition: `max-height ${timing}` }}
    >
      {/* shadow cast by the slot onto the sheet */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20 h-2.5"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.4) 0, transparent 10px)" }}
      />
      <div
        ref={ref}
        style={{
          background: "var(--bg)",
          clipPath: teeth(6),
          filter: "drop-shadow(0 18px 24px rgba(0,0,0,0.18)) drop-shadow(0 4px 6px rgba(0,0,0,0.12))",
          transform: `translateY(${shown - height}px)`,
          transition: `transform ${timing}`,
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------- machine ---------------- */

export const PrinterMachine: React.FC<{
  children: React.ReactNode;
  onClose: () => void;
  themeToggle: React.ReactNode;
}> = ({ children, onClose, themeToggle }) => {
  const [on, setOn] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[999] overflow-hidden bg-(--machine)">
      <button
        onClick={onClose}
        aria-label="Close the printer"
        title="Close the printer"
        className="absolute right-4 top-4 z-[1000] cursor-pointer rounded-[6px] border border-transparent p-1.5 text-(--muted) transition-colors duration-300 hover:border-(--border) hover:bg-(--hover) hover:text-(--title)"
      >
        <XIcon />
      </button>
      <div className="w-full max-w-[980px] mx-auto px-2 sm:px-8 pt-14 pb-24">
        <div
          className="relative z-20 mx-auto max-w-full motion-reduce:transition-none"
          style={{
            width: 940,
            transform: on ? "scale(1)" : "scale(0.82)",
            opacity: on ? 1 : 0,
            transition: "transform 420ms cubic-bezier(0.22, 1.2, 0.36, 1), opacity 420ms ease-out",
          }}
        >
          <div
            className="relative rounded-[20px] p-3"
            style={{
              background: "linear-gradient(180deg, #38362f 0%, #201f1a 100%)",
              boxShadow: "0 22px 34px -16px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            <div className="relative h-[140px]">{themeToggle}</div>
            <div
              className="relative mx-auto h-6 max-w-[95%] rounded-full"
              style={{ width: 880, background: "#000", border: "1px solid rgba(255,255,255,0.2)" }}
            >
              <Paper>{children}</Paper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- iris (closing the printer) ---------------- */

const EASE_OPEN = [0.76, 0, 0.24, 1] as const;
const EASE_CIRCLE = [0.65, 0, 0.35, 1] as const;
const EASE_DOT = [0.34, 1.4, 0.64, 1] as const;

/** A dot pops in, swells to a disc, then opens like an iris onto the page (page load, page changes, closing the printer). */
export const Iris: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [stage, setStage] = useState<"dot" | "circle" | "open">("dot");
  const [full, setFull] = useState(1400);

  const skip = reducedMotion();

  useEffect(() => {
    if (skip) {
      onDone();
      return;
    }
    const size = () => setFull(Math.hypot(innerWidth, innerHeight) / 2 + 8);
    size();
    addEventListener("resize", size);
    const timers = [
      setTimeout(() => setStage((s) => (s === "dot" ? "circle" : s)), 500),
      setTimeout(() => setStage("open"), 1200),
    ];
    return () => {
      removeEventListener("resize", size);
      timers.forEach(clearTimeout);
    };
  }, []);

  if (skip) return null;

  const open = stage === "open";
  const r = stage === "dot" ? 5 : stage === "circle" ? 64 : full;
  const transition = open
    ? { duration: 1.1, ease: EASE_OPEN }
    : stage === "circle"
      ? { duration: 0.55, ease: EASE_CIRCLE }
      : { duration: 0.45, ease: EASE_DOT };

  return (
    <>
      <style>{"body { overflow: hidden !important; }"}</style>
      <div className="fixed inset-0 z-[1001]" style={{ pointerEvents: open ? "none" : "auto" }} aria-hidden="true">
        <svg className="w-full h-full" width="100%" height="100%">
          <defs>
            <mask id="iris-hole">
              <rect width="100%" height="100%" fill="white" />
              <motion.circle cx="50%" cy="50%" fill="black" initial={{ r: 0 }} animate={{ r: open ? full : 0 }} transition={transition} />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="var(--bg)" mask="url(#iris-hole)" />
          <motion.circle
            cx="50%"
            cy="50%"
            fill="var(--fg)"
            initial={{ r: 0, opacity: 1 }}
            animate={{ r, opacity: open ? 0 : 1 }}
            transition={transition}
            onAnimationComplete={() => open && onDone()}
          />
        </svg>
      </div>
    </>
  );
};
